import { useEffect, useRef, useState } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Badge,
  Alert,
  Collapse,
  Table,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { HasPermission } from '../../rbac/HasPermission';
import { useBrand } from '../../brand/BrandProvider';
import { safeExternalUrl } from '../../utils/safeUrl';
import { usePolicy, usePolicyDiagnose, useActivatePolicyNow } from './usePolicy';
import { CreatePolicyVersionModal } from './CreatePolicyVersionModal';
import { DOC_TYPES } from './fixture';

/**
 * Policy · § 312j Pflicht-Dokumente Versionsverwaltung.
 * Spec: orbit-specs/done/policy-diagnose.md
 */
export function PolicyPage() {
  const brand = useBrand();
  const policy = usePolicy();
  const diagnose = usePolicyDiagnose(brand.currentBrandOrigin);
  const activate = useActivatePolicyNow();
  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(0);
  const [defaultDocType, setDefaultDocType] = useState('agb');
  const [diagnoseOpen, setDiagnoseOpen] = useState(false);
  const [pendingActivate, setPendingActivate] = useState(null);
  const autoOpened = useRef(false);

  // Diagnose initial aufklappen wenn irgendein Doc-Type !== 'ok' — nur einmal pro Page-Load
  useEffect(() => {
    if (autoOpened.current || !diagnose.data) return;
    const anyProblem = Object.values(diagnose.data.byDocType).some((d) => d.status !== 'ok');
    if (anyProblem) {
      autoOpened.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- bewusst: einmaliger Open-Trigger nach erstem Daten-Eintreffen
      setDiagnoseOpen(true);
    }
  }, [diagnose.data]);

  const onCreate = (docType) => {
    setDefaultDocType(docType);
    setCreateKey((k) => k + 1);
    setCreateOpen(true);
  };

  const onActivateConfirm = async () => {
    if (!pendingActivate) return;
    await activate.mutateAsync({ id: pendingActivate.id });
    setPendingActivate(null);
  };

  if (policy.isLoading) {
    return (
      <div>
        <PageHeader title="Pflicht-Dokumente (§ 312j BGB)" />
        <QueryLoadingState />
      </div>
    );
  }
  if (policy.isError) {
    return (
      <div>
        <PageHeader title="Pflicht-Dokumente (§ 312j BGB)" />
        <QueryErrorState error={policy.error} onRetry={() => policy.refetch()} />
      </div>
    );
  }

  const versions = policy.data?.versions ?? [];

  return (
    <div>
      <PageHeader
        title="Pflicht-Dokumente (§ 312j BGB)"
        subtitle="AGB · Datenschutz · Widerrufsbelehrung · Auftragsverarbeitung — Versionsverwaltung mit Diagnose"
        actions={
          <Button color={diagnoseOpen ? 'primary' : 'primary'} outline={!diagnoseOpen} onClick={() => setDiagnoseOpen(!diagnoseOpen)}>
            🐛 Diagnose {diagnoseOpen ? '▲' : '▼'}
          </Button>
        }
      />

      <Alert color="info" className="small">
        <strong>§ 312j BGB:</strong> Pflicht-Dokumente müssen vor Vertragsabschluss
        wirksam sein. Eine fehlende Version blockiert den Acceptance-Block im Checkout
        ({` `}<code>policy_not_configured</code>{` `}im Quote-Flow). "Jetzt aktivieren"
        setzt <code>effective_at = NOW</code> auf eine bestehende Version.
      </Alert>

      <Collapse isOpen={diagnoseOpen}>
        <DiagnosePanel
          query={diagnose}
          onActivate={(version) => setPendingActivate(version)}
          activatePending={activate.isPending}
        />
      </Collapse>

      <Row className="g-3 mt-1">
        {DOC_TYPES.map((dt) => {
          const docVersions = versions.filter((v) => v.documentType === dt.value);
          const diagnoseForDoc = diagnose.data?.byDocType?.[dt.value];
          return (
            <Col md="12" lg="6" key={dt.value}>
              <DocTypeCard
                docType={dt}
                versions={docVersions}
                diagnose={diagnoseForDoc}
                onCreate={() => onCreate(dt.value)}
                onActivate={(version) => setPendingActivate(version)}
                activatePending={activate.isPending}
              />
            </Col>
          );
        })}
      </Row>

      <CreatePolicyVersionModal
        key={createOpen ? `${defaultDocType}-${createKey}` : 'closed'}
        isOpen={createOpen}
        defaultDocType={defaultDocType}
        onClose={() => setCreateOpen(false)}
      />

      <ConfirmDialog
        isOpen={!!pendingActivate}
        title="Sofort als wirksam setzen?"
        body={
          pendingActivate ? (
            <>
              <p className="mb-2">
                Setzt <code>effective_at = NOW</code> auf Version{' '}
                <strong>{pendingActivate.version}</strong>{' '}
                ({pendingActivate.documentType}).
              </p>
              {pendingActivate.effectiveAt &&
                new Date(pendingActivate.effectiveAt) > new Date() && (
                  <Alert color="warning" className="small mb-0">
                    Bisherige Wirksamkeit: <code>{pendingActivate.effectiveAt}</code>{' '}
                    (Zukunft) → wird zu <strong>jetzt</strong>.
                  </Alert>
                )}
            </>
          ) : null
        }
        confirmLabel="Jetzt aktivieren"
        confirmColor="warning"
        busy={activate.isPending}
        onConfirm={onActivateConfirm}
        onCancel={() => setPendingActivate(null)}
      />
    </div>
  );
}

function DiagnosePanel({ query, onActivate, activatePending }) {
  if (query.isLoading) {
    return (
      <Card className="mb-3 border-info">
        <CardBody>Diagnose lädt…</CardBody>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card className="mb-3 border-danger">
        <CardBody>
          <QueryErrorState error={query.error} onRetry={() => query.refetch()} />
        </CardBody>
      </Card>
    );
  }
  if (!query.data) return null;
  const { brandOrigin, tenantId, serverNow, byDocType } = query.data;
  // dataUpdatedAt = Timestamp des letzten Query-Fetches (pure prop, kein Date.now() im Render)
  const browserDrift = query.dataUpdatedAt
    ? Math.abs(query.dataUpdatedAt - new Date(serverNow).getTime())
    : 0;
  const driftWarning = browserDrift > 5 * 60_000;

  return (
    <Card className="mb-3 border-info">
      <CardBody>
        <CardTitle tag="h5" className="mb-2">
          🐛 Diagnose
          <Badge color="info" pill className="ms-2">tenant {tenantId}</Badge>
        </CardTitle>
        <div className="mb-3 small">
          <div>
            <strong>Brand:</strong> <CopyChip value={brandOrigin} label="Brand-Origin" />
          </div>
          <div>
            <strong>Server-Zeit:</strong> <code>{serverNow}</code>
          </div>
          {driftWarning && (
            <Alert color="warning" className="small mt-2 mb-0 py-2">
              ⚠ Browser-Zeit weicht um {Math.round(browserDrift / 1000)}s von Server-Zeit ab —
              bei zeitnahen Aktivierungen kritisch prüfen.
            </Alert>
          )}
        </div>

        <Table size="sm" className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Doc-Type</th>
              <th style={{ width: 110 }}>Status</th>
              <th>Diagnose</th>
              <th style={{ width: 80 }}>Vers.</th>
              <th style={{ width: 140 }}>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {DOC_TYPES.map((dt) => {
              const d = byDocType[dt.value];
              if (!d) return null;
              const inForceVersion = d.versions.find((v) => v.isInForce);
              const futureVersion = d.status === 'future_only' ? d.versions[d.versions.length - 1] : null;
              return (
                <tr key={dt.value}>
                  <td><strong>{dt.label}</strong></td>
                  <td><DiagnoseStatusBadge status={d.status} /></td>
                  <td className="small text-muted">{d.reason}</td>
                  <td>{d.versions.length}</td>
                  <td>
                    {d.status === 'future_only' && futureVersion && (
                      <HasPermission perms={['policy.write']}>
                        <Button
                          color="warning"
                          size="sm"
                          onClick={() => onActivate(futureVersion)}
                          disabled={activatePending}
                        >
                          ⚡ Jetzt
                        </Button>
                      </HasPermission>
                    )}
                    {inForceVersion && d.status === 'ok' && (
                      <Badge color="success" pill style={{ fontSize: 10 }}>
                        v{inForceVersion.version}
                      </Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
}

function DiagnoseStatusBadge({ status }) {
  if (status === 'ok') return <Badge color="success" pill>✓ ok</Badge>;
  if (status === 'future_only') return <Badge color="warning" pill>⚠ future</Badge>;
  if (status === 'missing') return <Badge color="danger" pill>✗ missing</Badge>;
  return <Badge color="secondary" pill>{status}</Badge>;
}

function DocTypeCard({ docType, versions, diagnose, onCreate, onActivate, activatePending }) {
  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <CardTitle tag="h5" className="mb-0">{docType.label}</CardTitle>
            <p className="small text-muted mb-0">{docType.description}</p>
          </div>
          <HasPermission perms={['policy.write']}>
            <Button color="primary" size="sm" onClick={onCreate}>
              + Neue Version
            </Button>
          </HasPermission>
        </div>

        {diagnose?.status === 'missing' ? (
          <Alert color="danger" className="small mb-0">
            ✗ {diagnose.reason}
          </Alert>
        ) : (
          <Table size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Version</th>
                <th>Body-URL</th>
                <th style={{ width: 130 }}>Wirksam ab</th>
                <th style={{ width: 130 }}>Erstellt</th>
                <th style={{ width: 110 }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => {
                const inForce = diagnose?.versions?.find((x) => x.id === v.id)?.isInForce;
                const isFuture = new Date(v.effectiveAt) > new Date();
                return (
                  <tr key={v.id}>
                    <td>
                      <code>{v.version}</code>
                      {inForce && (
                        <Badge color="success" pill className="ms-2" style={{ fontSize: 10 }}>aktiv</Badge>
                      )}
                      {isFuture && !inForce && (
                        <Badge color="warning" pill className="ms-2" style={{ fontSize: 10 }}>future</Badge>
                      )}
                    </td>
                    <td>
                      {safeExternalUrl(v.bodyUrl) ? (
                        <a href={safeExternalUrl(v.bodyUrl)} target="_blank" rel="noopener noreferrer" className="small">
                          📄 öffnen
                        </a>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                    <td><RelativeTime iso={v.effectiveAt} /></td>
                    <td><RelativeTime iso={v.createdAt} /></td>
                    <td>
                      {isFuture && !inForce && (
                        <HasPermission perms={['policy.write']}>
                          <Button
                            color="warning"
                            outline
                            size="sm"
                            onClick={() => onActivate(v)}
                            disabled={activatePending}
                          >
                            ⚡ Jetzt
                          </Button>
                        </HasPermission>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}
